import {useCallback, useEffect, useRef, useState} from "react";
import {isRTL} from "../utils/rtl";

export function useTooltipPosition(delay: number, position: 'right' | 'bottom') {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const iconRef = useRef<HTMLDivElement | null>(null);
    const timeoutRef = useRef<number | null>(null);

    const showTooltip = useCallback(() => {
        timeoutRef.current = window.setTimeout(() => {
            if (iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect();

                setCoords({
                    top: position === 'right' ? rect.top + rect.height / 2 : rect.bottom + 10,
                    left: position === 'right'
                        ? (isRTL() ? rect.left - 10 : rect.right + 10)
                        : rect.left + rect.width / 2,
                });
            }
            setVisible(true);
        }, delay);
    }, [delay, position]);

    const hideTooltip = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setVisible(false);
    }, []);

    const updatePosition = useCallback(() => {
        if (!iconRef.current || !visible) return;
        const rect = iconRef.current.getBoundingClientRect();
        setCoords({
            top: position === 'right' ? rect.top + rect.height / 2 : rect.bottom + 10,
            left: position === 'right' ? rect.right + 10 : rect.left + rect.width / 2,
        });
    }, [visible]);

    useEffect(() => {
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition, true);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition, true);
        };
    }, [updatePosition]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return { iconRef, coords, visible, showTooltip, hideTooltip };
}